import Phaser from 'phaser';
import { COLORS, DISPLAY_FONT } from './theme';

export interface SpinnerOptions {
  values: string[];
  index?: number;
  width?: number;
  height?: number;
  fontSize?: string;
  onChange?: (value: string, index: number) => void;
}

// Scratch-style value spinner: orange value box with a green-up / red-down
// triangle pair on the right. Cycles through `values`.
export class Spinner extends Phaser.GameObjects.Container {
  private idx: number;
  private readonly values: string[];
  private readonly valueText: Phaser.GameObjects.Text;
  private readonly onChange?: (value: string, index: number) => void;

  constructor(scene: Phaser.Scene, x: number, y: number, opts: SpinnerOptions) {
    super(scene, x, y);
    scene.add.existing(this);

    this.values = opts.values;
    this.idx = Phaser.Math.Clamp(opts.index ?? 0, 0, opts.values.length - 1);
    this.onChange = opts.onChange;

    const w = opts.width ?? 130;
    const h = opts.height ?? 56;
    const triW = 26;
    const boxW = w - triW - 6;

    const g = scene.add.graphics();
    this.add(g);
    g.fillStyle(COLORS.accentOrange, 1);
    g.fillRoundedRect(-w / 2, -h / 2, boxW, h, 10);

    this.valueText = scene.add.text(-w / 2 + boxW / 2, 0, this.values[this.idx], {
      fontFamily: DISPLAY_FONT,
      fontSize: opts.fontSize ?? '26px',
      fontStyle: '600',
      color: '#ffffff',
    }).setOrigin(0.5);
    this.add(this.valueText);

    const triX = -w / 2 + boxW + 6 + triW / 2;
    const triH = h / 2 - 4;
    this.add(this.makeTriangle(scene, triX, -h / 4 - 1, triW, triH, 0x2fb62f, true, () => this.step(+1)));
    this.add(this.makeTriangle(scene, triX, h / 4 + 1, triW, triH, 0xc23b2f, false, () => this.step(-1)));
  }

  get value(): string {
    return this.values[this.idx];
  }

  get index(): number {
    return this.idx;
  }

  setIndex(index: number): void {
    this.idx = ((index % this.values.length) + this.values.length) % this.values.length;
    this.valueText.setText(this.values[this.idx]);
  }

  private step(delta: number): void {
    this.setIndex(this.idx + delta);
    this.onChange?.(this.value, this.idx);
  }

  private makeTriangle(
    scene: Phaser.Scene,
    x: number,
    y: number,
    w: number,
    h: number,
    color: number,
    up: boolean,
    onClick: () => void,
  ): Phaser.GameObjects.Container {
    const c = new Phaser.GameObjects.Container(scene, x, y);
    const g = scene.add.graphics();
    c.add(g);
    const dir = up ? -1 : 1;
    g.fillStyle(color, 1);
    g.fillTriangle(-w / 2, -dir * h / 2, w / 2, -dir * h / 2, 0, dir * h / 2);

    c.setInteractive(
      new Phaser.Geom.Rectangle(-w / 2 - 4, -h / 2 - 4, w + 8, h + 8),
      Phaser.Geom.Rectangle.Contains,
    );
    c.input!.cursor = 'pointer';
    c.on('pointerover', () => c.setScale(1.15));
    c.on('pointerout', () => c.setScale(1));
    c.on('pointerup', onClick);
    return c;
  }
}
